import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { EASE } from "./shared";
import { useLang } from "./i18n";
import { useTheme } from "./theme";

const HREFS = ["#how-it-works", "#platform", "#pricing", "#stories"];
const NAV_KEYS = ["how-it-works", "platform", "pricing", "stories"];

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

function LangToggle({ mobile = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}
      className={`t-border flex items-center rounded-full border p-0.5 ${mobile ? "" : ""}`}
    >
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}${mobile ? "-mobile" : ""}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "t-lang-active" : "t-lang-idle"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <button
      data-testid="theme-toggle"
      onClick={() => setTheme(theme === "light" ? "dark" : "light")}
      className="t-border t-text flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-200"
      aria-label="Toggle theme"
    >
      {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
    </button>
  );
}

export function Header({ start = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, images } = useLang();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    h();
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -90, opacity: 0 }}
      animate={start ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.7, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "t-header shadow-[0_8px_32px_rgba(0,0,0,0.12)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#top" data-testid="header-logo" onClick={(e) => go(e, "#top")} className="flex items-center gap-2.5">
          <img
            src={images.logoMark}
            alt="Settld logo"
            className="h-10 w-10 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
          />
          <span className="t-text font-sans text-[22px] font-extrabold tracking-tight">
            Settld<span className="text-aqua-500">.</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {t.nav.links.map((label, i) => (
            <a
              key={HREFS[i]}
              href={HREFS[i]}
              data-testid={`nav-link-${NAV_KEYS[i]}`}
              onClick={(e) => go(e, HREFS[i])}
              className="t-nav-link text-sm font-medium"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          <ThemeToggle />
          <button
            data-testid="nav-login-btn"
            onClick={(e) => go(e, "#quote")}
            className="t-nav-link rounded-full px-4 py-2.5 text-sm font-semibold"
          >
            {t.nav.login}
          </button>
          <button
            data-testid="nav-start-btn"
            onClick={(e) => go(e, "#quote")}
            className="group flex items-center gap-1.5 rounded-full bg-maple px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#C23225]"
          >
            {t.nav.start}
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="t-text flex h-10 w-10 items-center justify-center rounded-xl"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="t-header overflow-hidden lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 pb-6 pt-2">
              {t.nav.links.map((label, i) => (
                <a
                  key={HREFS[i]}
                  href={HREFS[i]}
                  data-testid={`mobile-nav-${NAV_KEYS[i]}`}
                  onClick={(e) => go(e, HREFS[i])}
                  className="t-nav-link rounded-xl px-4 py-3 text-base font-medium"
                >
                  {label}
                </a>
              ))}
              <div className="mt-3 flex items-center gap-3">
                <LangToggle mobile />
                <button
                  data-testid="mobile-start-btn"
                  onClick={(e) => go(e, "#quote")}
                  className="flex-1 rounded-full bg-maple px-5 py-3 text-sm font-bold text-white"
                >
                  {t.nav.start}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
